import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { menuChildrenAnimation } from '@osos/shared/legacy-common';
import { ItemMenu } from '@osos/shared/types';
import { ComponentBase } from '@osos/shared/utility';
import { filter, takeUntil } from 'rxjs/operators';
import { MenuService } from '../../services/menu.service';
import { MainComponent } from '../main/main.component';

// TODO: IMPORTANT: This component MUST be reviewed carefully later from performance point of view.
// also The component needs to be refactored sans we don't support some cases.
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[menu-item]',
  templateUrl: './menu-item.component.html',
  host: {
    '[class.layout-root-menuitem]': 'root',
    '[class.active-menuitem]': 'isActiveItem || hasActiveItem',
    '[class.expanded-menuitem]': 'itemsExpanded',
  },
  animations: menuChildrenAnimation,
  // IMPORTANT: Since this component is always rendered, we should keep it updated only OnPush.
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MenuItemComponent
  extends ComponentBase
  implements OnInit, OnDestroy
{
  @Input() item: ItemMenu;

  @Input() index: number;

  @Input() root: boolean;

  @Input() parentKey: string;

  @Input() fixed = false;

  @Input() rtlDir = false;

  get isStatic(): boolean {
    return this.app.isStatic();
  }

  get isOverlay(): boolean {
    return this.app.isOverlay();
  }

  @ViewChild('subitemsList') subitemsList: ElementRef<HTMLUListElement>;

  /** Whether the item has any active sub-item */
  hasActiveItem = false;

  /** Whether the item is active itself */
  isActiveItem = false;

  /** Whether the sub-items are expanded or not */
  itemsExpanded = false;

  key: string;

  trackByFn = (_, item) => item.id;

  constructor(
    public app: MainComponent,
    public router: Router,
    private cdr: ChangeDetectorRef,
    private menuService: MenuService,
  ) {
    super();
    this.menuService.menuSource$
      .pipe(takeUntil(this.destroy$))
      .subscribe((key) => {
        // deactivate current active menu
        if (this.key !== key && key.indexOf(this.key) !== 0) {
          this.closeOrOpenSubmenu();
        }
      });

    this.menuService.resetSource$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.closeOrOpenSubmenu();
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        if (this.app.isHorizontal() || this.app.isSlim()) {
        } else {
          this.updateActiveStateFromRoute();
        }
      });
  }

  ngOnInit() {
    if (!(this.app.isHorizontal() || this.app.isSlim())) {
      this.updateActiveStateFromRoute();
    }

    this.key = this.parentKey
      ? this.parentKey + '-' + this.index
      : String(this.index);
  }

  updateActiveStateFromRoute() {
    if (this.item.items) {
      this.hasActiveItem = this._hasActiveChild(this.item.items);
    } else {
      this.isActiveItem = this._isActiveRouterLink(this.item);
    }

    this.itemsExpanded = this.hasActiveItem || this.isActiveItem;
    this.cdr.markForCheck();
  }

  itemClick(event: Event): any {
    // avoid processing disabled items
    if (this.item.disabled) {
      event.preventDefault();
      return true;
    }

    // navigate with hover in horizontal mode
    if (this.root && this.app.isHorizontal()) {
      this.app.menuHoverActive = !this.app.menuHoverActive;
    }

    // notify other items
    this.menuService.onMenuStateChange(this.key);

    // execute command
    if (this.item.command) {
      this.item.command({ originalEvent: event, item: this.item });
    }

    if (this.item.items) {
      this.closeOrOpenSubmenu(!this.itemsExpanded);
      if (this.app.isSlim() && this.app.isDesktop()) {
        this.prepareFixedMenu(event);
      }
    } else {
      // activate item
      if (this.app.isHorizontal() || this.app.isSlim()) {
        this.menuService.reset();
        this.app.menuHoverActive = false;
      }

      if (!this.app.isStatic()) {
        this.app.menuActive = false;
      }
      this.app.mobileMenuActive = false;
    }

    this.removeActiveInk(event);
    return false;
  }

  onMouseEnter(event) {
    // activate item on hover
    if (
      (this.app.isHorizontal() || this.app.isSlim() || this.app.isIcons()) &&
      this.app.isDesktop()
    ) {
      if (this.app.menuHoverActive) {
        this.menuService.onMenuStateChange(this.key);
      }
      if (this.app.isIcons()) {
        this.prepareFixedMenu(event);
      }
    }
  }

  @HostListener('mouseleave', ['$event'])
  onMouseleave() {
    if (this.app.isHorizontal() || this.app.isSlim() || this.app.isIcons()) {
      if (this.app.menuHoverActive) {
        this.menuService.onMenuStateChange(this.key);
      }
    }
    if (this.subitemsList?.nativeElement && this.app.isIcons()) {
      setTimeout(() => {
        this.subitemsList?.nativeElement.removeAttribute('style');
      }, 100);
    }
  }

  prepareFixedMenu(event): void {
    const padding = 16;
    const clientRect = (event.target as HTMLElement).getBoundingClientRect();
    if (this.fixed && this.subitemsList?.nativeElement) {
      this.subitemsList.nativeElement.style.top = `${
        clientRect.top - clientRect.height + padding
      }px`;
      if (this.rtlDir) {
        const width = window.innerWidth;
        const position = width - clientRect.right;
        this.subitemsList.nativeElement.style.right = `${
          clientRect.width + position
        }px`;
      } else {
        this.subitemsList.nativeElement.style.left = `${
          clientRect.width + clientRect.left - padding
        }px`;
      }
      setTimeout(() => {
        const listReact =
          this.subitemsList.nativeElement.getBoundingClientRect();
        const wHeight = window.innerHeight;
        if (listReact.bottom >= wHeight - padding) {
          this.subitemsList.nativeElement.style.top = 'unset';
          this.subitemsList.nativeElement.style.bottom = `${padding}px`;
        }
      }, 100);
    }
  }
  removeActiveInk(event: Event): void {
    const currentTarget = event.currentTarget as HTMLElement;
    setTimeout(() => {
      if (currentTarget) {
        const activeInk = currentTarget.querySelector('.p-ink-active');
        if (activeInk) {
          if (activeInk.classList) {
            activeInk.classList.remove('p-ink-active');
          } else {
            activeInk.className = activeInk.className.replace(
              new RegExp(
                '(^|\\b)' + 'p-ink-active'.split(' ').join('|') + '(\\b|$)',
                'gi',
              ),
              ' ',
            );
          }
        }
      }
    }, 401);
  }

  closeOrOpenSubmenu(isActive = false): void {
    this.itemsExpanded = isActive;
    this.cdr.markForCheck();
  }

  private _hasActiveChild(items: ItemMenu[]): boolean {
    return items.some((item) =>
      item.items
        ? this._hasActiveChild(item.items)
        : this._isActiveRouterLink(item),
    );
  }

  private _isActiveRouterLink(item: ItemMenu): boolean {
    const url = this.router.createUrlTree([item.routerLink], {
      queryParams: item.queryParams,
    });

    return this.router.isActive(url, {
      paths: 'subset',
      queryParams: 'subset',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }
}
